// components/AdminPage.jsx
import { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import {
  initializeDefaultUser,
  isAnonymousUser,
  isSameAccount,
  startAdminSession,
  touchAdminActivity,
  clearAdminActivity,
  getAdminSession,
  isAdminSessionExpired,
  isSuperAdmin,
} from '../utils/authServiceUtil';
import { useConfig } from '../context/ConfigContext';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { CONFIG_ADMIN_EMAILS, ADMIN_IDLE_TIMEOUT_MS } from '../constants/bookingConst';
import { loadingSpinner } from '../utils/bookingUtils';
import { setErrorLogScreen } from '../utils/errorLogUtil';
import { ensureDeployStatusBaseline } from '../utils/redeployUtil';
import AdminSidebar from './AdminSidebar';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import '../css/AdminPage.css';
// Brand mark via BrandLogo, sized by LandingPage's lp-logo class.
import '../css/LandingPage.css';
import AdminBooking from './AdminBooking';
import AdminOpenPlay from './AdminOpenPlay';
import AdminEquipment from './AdminEquipment';
import AdminExport from './AdminExport';
import AdminEvents from './AdminEvents';
import AdminConfig from './AdminConfig';
import AdminUser from './AdminUser';
import AdminAuditTrail from './AdminAuditTrail';
import AdminErrorLogs from './AdminErrorLogs';
import AdminPromo from './AdminPromo';
import BrandLogo from './BrandLogo';

const AdminPage = () => {
  const { config, loading: configLoading } = useConfig();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginError, setAdminLoginError] = useState('');
  // The account actually signed in. Kept separately from `adminEmail`,
  // which is just the login form's input and gets cleared on success —
  // this needs to survive for the whole session to gate the Config screen
  // (and to show the right account on the User screen).
  const [signedInEmail, setSignedInEmail] = useState('');
  // True until we've checked whether a previous admin session should be
  // resumed — without it, a refresh flashes the login form for a moment
  // before the restore lands.
  const [restoringSession, setRestoringSession] = useState(true);

  // Only these accounts see/reach Config (see CONFIG_ADMIN_EMAILS).
  const isConfigAdmin = CONFIG_ADMIN_EMAILS
    .map(e => e.toLowerCase())
    .includes((signedInEmail || '').trim().toLowerCase());

  // Only super-admins may SAVE /config — the database rules enforce it, so
  // the screens that write it (Config, Events and Packages, Promo) are shown
  // to those accounts alone rather than offering a Save that is denied.
  const isConfigWriter = isSuperAdmin(signedInEmail);

  const [showSidebar, setShowSidebar] = useState(false);
  const [adminScreen, setAdminScreen] = useState('booking');

  // Config -> System. `!== false` rather than a truthiness check: a config
  // saved before this setting existed has no such key, and that has to keep
  // timing out rather than silently becoming a permanent session.
  const idleTimeoutEnabled = config?.adminIdleTimeoutEnabled !== false;

  // Resume an admin session across a refresh. Firebase persists the login
  // itself, but `isAuthenticated` is React state and dies with the page —
  // which is why refreshing used to bounce back to the login form.
  //
  // What proves an admin logged in is the stored session record, NOT just
  // "is someone signed in": every visitor is silently signed in anonymously
  // at boot (AuthGuard), purely to satisfy the database rules' `auth !=
  // null`, so an anonymous session on its own says nothing about admin-ness.
  //
  // The Firebase user is still checked, but only to confirm it's the same
  // account the record was opened for; a mismatch (another tab logged out,
  // or someone else signed in) means the record is stale. Anything that
  // isn't resumable is dropped and handed back to an anonymous session, so
  // the guest side of the app keeps working in this tab.
  //
  // Waits for the config to resolve first, because whether the idle window
  // applies at all is a config setting — restoring against the default while
  // it loads would log out an admin whose club has the timeout turned off.
  useEffect(() => {
    if (configLoading) return undefined;

    let cancelled = false;
    setRestoringSession(true);

    const restoreAdminSession = async () => {
      // null when missing, or idle-expired while the timeout is enabled
      const session = getAdminSession(idleTimeoutEnabled);

      try {
        // Awaiting this rather than reading auth.currentUser avoids the
        // boot race: Firebase resolves persisted sessions asynchronously,
        // so currentUser is briefly null even when a session exists.
        const user = await initializeDefaultUser();
        const email = user?.email || '';

        if (session && isSameAccount(email, session.email)) {
          touchAdminActivity();
          if (!cancelled) {
            setSignedInEmail(email);
            setIsAuthenticated(true);
          }
          return;
        }

        clearAdminActivity();
        // A real (non-anonymous) login still signed in with no live session
        // is a leftover admin login (expired, or ended in another tab) —
        // swap it back to an anonymous guest session.
        if (!isAnonymousUser(user)) {
          await signOut(auth);
          await initializeDefaultUser();
        }
      } catch (error) {
        console.error('Error restoring admin session:', error);
      } finally {
        if (!cancelled) setRestoringSession(false);
      }
    };

    restoreAdminSession();
    return () => { cancelled = true; };
    // Deliberately keyed on configLoading only — this is a one-shot restore,
    // and re-running it when an admin toggles the timeout mid-session would
    // re-check a session that's already live. A stalled restore is recovered
    // by the timeout screen's Refresh, which hard-reloads the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  // Keep the session alive while the admin is actually using the page, and
  // end it once they've been idle past the timeout. The visibilitychange
  // check is what catches a tab left open in the background: nothing fires
  // while it's hidden, so the elapsed time is only noticed on return.
  //
  // With the timeout turned off (Config -> System) the activity listeners
  // still run and the expiry checks don't. Recording activity either way is
  // what makes re-enabling the setting graceful: the clock resumes from the
  // admin's last real interaction instead of from whenever they logged in,
  // which would otherwise sign out everyone still working.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const expireIfIdle = () => {
      if (isAdminSessionExpired(idleTimeoutEnabled)) handleLogout();
    };
    // Throttled — these events fire constantly and every one would
    // otherwise be a localStorage write.
    let lastTouch = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch > 30000) {
        lastTouch = now;
        touchAdminActivity();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') expireIfIdle();
      else touchAdminActivity();
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    // No polling when the timeout is off — there's nothing for it to find,
    // and expireIfIdle would be a wasted call every minute for as long as
    // the dashboard stays open.
    const interval = idleTimeoutEnabled ? setInterval(expireIfIdle, 60000) : null;

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, idleTimeoutEnabled]);

  // Seeds the redeploy baseline the first time an admin signs in on a club
  // that has none. Without /deployStatus the pending-changes banner stays
  // hidden, and the banner is the only route to the Redeploy button — so a
  // club whose postdeploy hook never ran would have no way in. Covers both
  // sign-in paths (the form and a restored session) by watching the flag
  // they both set, rather than duplicating the call in each.
  //
  // Fire-and-forget on purpose: ensureDeployStatusBaseline never throws and
  // nothing on this screen waits for its result.
  useEffect(() => {
    if (!isAuthenticated) return;
    ensureDeployStatusBaseline();
  }, [isAuthenticated]);

  const handleLogin = async () => {
    if (!adminEmail || !adminPassword) {
      setAdminLoginError('Please enter both email and password.');
      return;
    }

    try {
      setAdminLoginError('');

      // Use raw password - Firebase Auth handles hashing internally
      const userCredential = await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

      // Capture from the credential rather than the form field — the form
      // is cleared right below, and this is what gates the Config screen.
      const authenticatedEmail = userCredential.user?.email || adminEmail;
      setSignedInEmail(authenticatedEmail);
      setIsAuthenticated(true);
      // Opens the idle window and records WHICH account it belongs to —
      // without this the session would read as expired on the very next
      // refresh, and the restore above would have nothing to match against.
      startAdminSession(authenticatedEmail);
      setAdminEmail('');
      setAdminPassword('');
    } catch (error) {
      console.error("Firebase admin sign-in error:", error.code);
      setAdminPassword('');

      if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/user-not-found' ||
        error.code === 'auth/wrong-password'
      ) {
        setAdminLoginError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-email') {
        setAdminLoginError('Please enter a valid email format.');
      } else {
        setAdminLoginError('Login failed. Please try again.');
      }
    }
  };

  const handleLogout = async () => {
    // Reset UI state first so an expiry can't leave the dashboard on
    // screen if the sign-out network call is slow or fails.
    clearAdminActivity();
    setIsAuthenticated(false);
    setSignedInEmail('');
    setAdminScreen('booking');
    try {
      await signOut(auth);
      // Hand the app back an anonymous guest session — Firebase rules gate
      // on auth != null, so leaving nobody signed in would break reads for
      // the guest side of the app in this tab. A real admin login is always
      // a distinct account from the anonymous guest identity now, so this
      // is no longer skippable the way it was with the old shared account.
      await initializeDefaultUser();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  /**
   * Switches the admin screen and tags subsequent error-log entries with it.
   * The tag is what turns "something threw on this deployment" into "something
   * threw on the Export screen" — the URL alone can't say, because the admin
   * dashboard is a single route.
   * @param {string} screen
   * @returns {void}
   */
  const handleScreenChange = (screen) => {
    setAdminScreen(screen);
    setErrorLogScreen(`admin/${screen}`);
  };

  const renderAdminScreen = () => {
    switch (adminScreen) {
      case 'openplay': return <AdminOpenPlay />;
      case 'equipment': return <AdminEquipment />;
      case 'export': return <AdminExport />;
      case 'events': return isConfigWriter ? <AdminEvents /> : <AdminBooking />;
      // Re-checked here, not just in the sidebar — hiding a menu entry
      // isn't a guard on its own, and this keeps the screen unreachable if
      // adminScreen is ever set to 'config' some other way.
      case 'config': return isConfigWriter ? <AdminConfig /> : <AdminBooking />;
      // Open to every signed-in admin, not just CONFIG_ADMIN_EMAILS: the
      // point of an audit trail is that the people doing the work can see
      // what was done, and restricting it to the owner account makes it a
      // report rather than a shared record. It stays read-only for everyone
      // — nothing on this screen can alter a booking or the log itself.
      case 'audit': return <AdminAuditTrail />;
      // Gated like Config, and re-checked here rather than trusting the
      // sidebar's filter: the entries carry page URLs, admin emails and stack
      // traces from every visitor session, which is owner-account material.
      case 'errorlogs': return isConfigAdmin ? <AdminErrorLogs /> : <AdminBooking />;
      // Gated like Config: a promo changes what guests pay, so a non-owner
      // admin who reaches this id falls back to the booking screen rather
      // than getting an editor whose saves the rules would reject.
      case 'promo': return isConfigWriter ? <AdminPromo /> : <AdminBooking />;
      case 'user': return <AdminUser adminEmail={signedInEmail} />;
      case 'booking':
      default: return <AdminBooking />;
    }
  };

  // Watchdog over the whole admin boot (config read + session restore), so a
  // stalled Firebase shows a Refresh instead of an endless spinner. Refresh
  // hard-reloads the page, which re-runs the whole boot from scratch.
  const booting = configLoading || restoringSession;
  const { timedOut: bootTimedOut } = useLoadingTimeout(booting);

  // Same reason as BookingApp in App.jsx: ConfigProvider seeds `config`
  // with DEFAULT_CONFIG, so rendering before it resolves briefly shows the
  // placeholder club name/logo on the login card before swapping to the
  // real ones.
  // Also waits on the admin-session restore, so a refresh doesn't flash
  // the login form before the previous session is resumed.
  //
  // Both halves can stall forever rather than fail: an unreachable Firebase
  // never calls onValue back, and the Auth SDK applies no client-side timeout
  // to signOut/sign-in. Checked BEFORE `booting` for the same reason App.jsx
  // checks configTimedOut first — once the admin has been told the connection
  // failed, a late response must not yank the screen away underneath them.
  if (bootTimedOut) {
    // Modal over the boot spinner the admin was watching — the dashboard
    // itself cannot render until the boot finishes.
    return (
      <>
        {loadingSpinner('Preparing your experience', config?.logoUrl)}
        <ConnectionErrorScreen />
      </>
    );
  }

  if (booting) {
    return loadingSpinner('Preparing your experience', config?.logoUrl);
  }

  // Show login form if not authenticated — jassen-themed, matching the
  // "Admin login" reference screen (light card over the cream page
  // background, teal accents). Only this pre-auth screen uses the jassen
  // look; the authenticated dashboard below keeps its existing dark theme.
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <header className="alp-header">
          <div className="alp-brand" onClick={() => window.location.href = '/'} role="button" tabIndex={0}>
            <BrandLogo src={config?.logoUrl} alt={config?.courtName || 'Court'} className="lp-logo" />
            <span className="alp-brand-name">{config?.courtName || 'Your Court Name'}</span>
          </div>
          <span className="alp-badge">Admin</span>
        </header>

        <div className="alp-body">
          <div className="alp-card">
            <h1 className="alp-title">Admin login</h1>
            {/* Says what will actually happen, which depends on the
                Config -> System setting — promising a 30-minute timeout to a
                club that has turned it off would just be wrong. */}
            <p className="alp-subtitle">
              Sign in to manage court availability.{' '}
              {idleTimeoutEnabled
                ? `You'll stay signed in on this device until ${Math.round(ADMIN_IDLE_TIMEOUT_MS / 60000)} minutes of inactivity.`
                : "You'll stay signed in on this device until you log out."}
            </p>

            <div className="alp-field">
              <label htmlFor="admin-username">Username</label>
              <input
                id="admin-username"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                autoFocus
              />
            </div>

            <div className="alp-field">
              <label htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
              />
            </div>

            {adminLoginError && <p className="alp-error">{adminLoginError}</p>}

            <button type="button" className="alp-submit" onClick={handleLogin}>
              Log in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show admin dashboard if authenticated
  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div className="header-left">
          <h5>{config?.courtName || 'Court Booking'} - Admin</h5>
        </div>
        <div className="header-right">
          {/* Styling lives in AdminPage.css. It used to be inline, which beat
              the mobile media queries there — the button never actually
              shrank on small screens. */}
          <button className="header-btn" onClick={() => setShowSidebar(true)}>
            <span>☰</span>
            <span>Menu</span>
          </button>
        </div>
      </header>

      <AdminSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        activeScreen={adminScreen}
        onScreenChange={handleScreenChange}
        onLogout={handleLogout}
        isConfigAdmin={isConfigAdmin}
        isConfigWriter={isConfigWriter}
      />

      <main className="admin-page-content">
        {renderAdminScreen()}
      </main>
    </div>
  );
};

export default AdminPage;
